import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://bitemark-backend-production.up.railway.app/api";

export default function Waitlist() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [form, setForm] = useState({ name: "", email: "", phone: "", botField: "" });
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.email.trim()) return;
    setStatus("loading");
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim() || null,
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          source: "bitemarkapp.com",
          botField: form.botField,
        }),
      });

      if (res.status === 409) {
        setStatus("success");
        setMessage("You’re already on the waitlist.");
        return;
      }

      if (!res.ok) {
        throw new Error("Waitlist request failed");
      }

      setStatus("success");
      setForm({ name: "", email: "", phone: "", botField: "" });
      confetti({
        particleCount: 120,
        spread: 65,
        startVelocity: 38,
        origin: { y: 0.6 },
      });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F4] text-[#2B1A14] antialiased">
      <header className="sticky top-0 w-full bg-[#FFF8F4]/95 backdrop-blur-md z-50 border-b border-[#F7D7C7]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-b from-[#FF9A5A] via-[#FF6B6B] to-[#FF5F52] flex items-center justify-center shadow-lg">
              <img src="/BiteMark.png" alt="BiteMark logo" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <div className="font-bold text-lg">BiteMark</div>
              <div className="text-xs text-[#7A3E2E] -mt-0.5">
                Find it • Bite it • Remember it
              </div>
            </div>
          </Link>

          <Link to="/" className="text-sm font-semibold text-[#7A3E2E] hover:text-[#FF4D4D] transition">
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 20 }}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
            Join the BiteMark waitlist.
          </h1>
          <p className="mt-4 text-[#5C2F24] max-w-xl">
            Get early access, priority invites, and the first drop of BiteMark Pro perks.
          </p>
          <div className="mt-6 p-4 rounded-2xl bg-[#FFF1EB] border border-[#FFD1C2] text-sm text-[#7A3E2E] font-semibold">
            We’ll only email you about launch updates. No spam.
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 20, delay: 0.1 }}
          className="bg-white border border-[#FFD1C2] shadow-xl rounded-3xl p-6"
        >
          <form
            name="bitemark-waitlist"
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <input
              type="text"
              name="bot-field"
              value={form.botField}
              onChange={(e) => setForm((prev) => ({ ...prev, botField: e.target.value }))}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />
            <div>
              <label className="text-sm font-semibold text-[#7A3E2E]">Name</label>
              <input
                name="name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ayman"
                className="mt-2 w-full rounded-xl border border-[#F0B9A8] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D4D]/40"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[#7A3E2E]">Email</label>
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="you@email.com"
                className="mt-2 w-full rounded-xl border border-[#F0B9A8] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D4D]/40"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[#7A3E2E]">
                Phone <span className="text-xs text-[#A36A5A]">(optional)</span>
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="mt-2 w-full rounded-xl border border-[#F0B9A8] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D4D]/40"
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full rounded-2xl bg-[#FF4D4D] py-3 font-semibold text-white shadow-lg hover:shadow-xl transition disabled:opacity-70"
            >
              {status === "loading" ? "Joining..." : "Join Waitlist"}
            </button>

            {status === "success" && (
              <p className="text-sm font-semibold text-[#1E8E5A]">
                {message || "You’re in! We’ll email you when BiteMark launches."}
              </p>
            )}
            {status === "error" && (
              <p className="text-sm font-semibold text-[#B23B3B]">
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </motion.div>
      </main>
    </div>
  );
}
